// @ts-check

/** @type {import('tailwindcss').Config} */
module.exports = {
  content: ['./src/**/*.{ts,tsx,js,jsx}'],
  theme: {
    borderRadius: {
      none: '0',
      xs: '0.25rem',
      sm: '0.5rem',
      DEFAULT: '1rem',
      lg: '2rem',
      full: '9999px',
    },
    colors: ({ theme }) => theme('colorsTw'),
    /**
     * The 'colors' key will be overwritten in ThemeProvider by 'colors' of old theme, so we have to expose 'colorsTw' in Styled theme
     *
     * @type {import('styled-components').ColorsTw}
     */
    colorsTw: {
      primary: {
        DEFAULT: '#08124C',
        dark: '#060E3D',
        medium: '#393F67',
        light: '#A6A8B9',
        lighter: '#ECECEC',
        lightest: '#F9F9FA',
      },
      secondary: {
        DEFAULT: '#0097FF',
        dark: '#0078CA',
        light: '#59BBFF',
        lighter: '#EAF6FF',
        lightest: '#F3FAFF',
      },
      tertiary: {
        DEFAULT: '#BE78FF',
        dark: '#A15AE2',
        light: '#DEBAFF',
        lightest: '#FAF4FF',
      },
      indigo: '#000A4F',
      vididBlue: '#04E1FF',
      gray: {
        95: '#08124C',
        70: '#777B96',
        50: '#AEB0BE',
        30: '#C4C6CD',
        15: '#D8D9DE',
        5: '#E9EAEF',
      },
      blue: {
        DEFAULT: '#252F42',
        dark: '#121721',
        darker: '#171F2E',
        darkest: '#1A2231',
        light: '#4E586D',
        lighter: '#979FAF',
      },
      green: {
        DEFAULT: '#119E6F',
      },
      red: {
        DEFAULT: '#EB3C1A',
      },
      white: '#FFFFFF',
      error: {
        DEFAULT: '#CB010D',
        dark: '#97022F',
        light: '#FEBED1',
        lightest: '#FFE8EF',
      },
      warning: {
        DEFAULT: '#FFC014',
        light: '#FDF5D6',
      },
      success: {
        DEFAULT: '#00C64F',
        light: '#ECFFE7',
      },
      current: 'currentColor',
      transparent: 'transparent',
    },
    fill: ({ theme }) => ({
      current: 'currentColor',
      tertiary: theme('colors.tertiary'),
    }),
    fontFamily: {
      sans: 'Libre Franklin, sans-serif',
      mono: '"Courier Prime", monospace',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.75rem',
      base: '0.875rem',
      lg: '1rem',
      xl: '1rem',
      enlarged: '1.125rem',
      '2xl': '1.25rem',
      '3xl': '1.5rem',
      '4xl': '2rem',
      '5xl': '2.5rem',
      'root-base': '16px',
      'root-sm': '16px',
      'root-lg': '16px',
      'root-xl': '16px',
    },
    fontWeight: {
      light: '300',
      normal: '400',
      semibold: '500',
      medium: '600',
      bold: '800',
    },
    flexGrow: {
      0: '0',
      DEFAULT: '1',
    },
    // e.g. tw="leading-snug"
    lineHeight: {
      4: '1rem',
      none: '1',
      snug: '1.4',
      normal: '2',
    },
    gradients: {
      black: ['transparent', 'black'],
    },
    screens: {
      sm: '768px',
      md: '992px',
      lg: '1280px',
      xl: '1440px',
    },
    spacing: {
      0: '0rem',
      quarter: '0.125rem',
      half: '0.25rem',
      1: '0.5rem',
      2: '1rem',
      3: '1.5rem',
      4: '2rem',
      5: '2.5rem',
      6: '3rem',
      7: '3.5rem',
      8: '4rem',
      9: '4.5rem',
      10: '5rem',
      11: '5.5rem',
      12: '6rem',
      13: '6.5rem',
      14: '7rem',
      15: '7.5rem',
      16: '8rem',
      17: '8.5rem',
      18: '9rem',
      19: '9.5rem',
      20: '10rem',
      21: '10.5rem',
      22: '11rem',
      23: '11.5rem',
      24: '12rem',
      25: '12.5rem',
      26: '13rem',
      27: '13.5rem',
      28: '14rem',
      29: '14.5rem',
      30: '15rem',
      31: '15.5rem',
      32: '16rem',
      33: '16.5rem',
      34: '17rem',
      35: '17.5rem',
      36: '18rem',
      37: '18.5rem',
      38: '19rem',
      39: '19.5rem',
      40: '20rem',
      41: '20.5rem',
      42: '21rem',
      43: '21.5rem',
      44: '22rem',
      45: '22.5rem',
      46: '23rem',
      47: '23.5rem',
      48: '24rem',
      49: '24.5rem',
      50: '25rem',
      51: '25.5rem',
      52: '26rem',
      53: '26.5rem',
      54: '27rem',
      55: '27.5rem',
      56: '28rem',
      57: '28.5rem',
      58: '29rem',
      59: '29.5rem',
      60: '30rem',
      '1/5': '20%',
      '-1/5': '-20%',
    },
    transitionDuration: {
      fast: '200ms',
      normal: '400ms',
      slow: '700ms',
    },
    extend: {
      animation: {
        'exit-right': 'exit 250ms ease',
        'enter-right': 'enter 250ms ease',
        'fade-in': 'fadeIn 250ms ease',
        'fade-out': 'fadeOut 250ms ease',
      },
      keyframes: {
        exit: {
          '0%': { transform: 'translateX(0%)' },
          '100%': { transform: 'translateX(100%)' },
        },
        enter: {
          '0%': { transform: 'translateX(100%)' },
          '100%': { transform: 'translateX(0%)' },
        },
        fadeIn: {
          '0%': { opacity: '0' },
          '100%': { opcaity: '1' },
        },
        fadeOut: {
          '0%': { opacity: '1' },
          '100%': { opacity: '0' },
        },
      },
      boxShadow: {
        DEFAULT: '0 4px 16px rgba(53, 0, 123, 0.05)',
        md: '0 8px 32px rgba(53, 0, 123, 0.05)',
        card: '0 4px 16px rgba(53, 0, 123, 0.05), 0 8px 32px rgba(53, 0, 123, 0.05)',
        flat: '0 0 0 0.2rem',
        outline: `0 0 0 1px #0081FF`,
        none: 'none',
      },
      backgroundSize: {
        3: '1.5rem',
      },
      bgOpacity: {
        20: '0.2',
        40: '0.4',
        60: '0.6',
        80: '0.8',
      },
      borderWidth: {
        1: '1px',
        5: '5px',
        DEFAULT: '2px',
      },
      height: {
        '24px': '24px',
        '48px': '48px',
        max: 'max-content',
        'min-content': 'min-content',
      },
      inset: {
        '1/2': '50%',
      },
      rotate: {
        '-15': '-15deg',
      },
      width: {
        'full-double': '200%',
      },
      maxWidth: {
        'modal-small': '372px',
        'modal-medium': '467px',
        'modal-large': '620px',
      },
      opacity: {
        10: '0.1',
        20: '0.2',
        40: '0.4',
        60: '0.6',
        70: '0.7',
        80: '0.8',
      },
      ringWidth: {
        DEFAULT: '4px',
      },
      stroke: {
        primary: '#08124C',
        secondary: '#0097FF',
        vididBlue: '#04E1FF',
      },
      spacing: {
        finger: '2.75rem',
      },
      zIndex: {
        '-1': '-1',
        1: '1',
        overlay: '9999',
      },
      /** @param utils {import('tailwindcss/types/config').PluginUtils} */
      typography: ({ theme }) => ({
        DEFAULT: {
          css: [
            {
              '--tw-prose-body': '#fff',
              '--tw-prose-headings': '#fff',
              '--tw-prose-lead': 'inherit',
              '--tw-prose-links': 'unset',
              '--tw-prose-bold': 'unset',
              '--tw-prose-counters': 'unset',
              '--tw-prose-bullets': 'unset',
              '--tw-prose-hr': 'unset',
              '--tw-prose-quotes': 'unset',
              '--tw-prose-quote-borders': 'unset',
              '--tw-prose-captions': 'unset',
              '--tw-prose-code': '#fff',
              '--tw-prose-pre-code': '#fff',
              '--tw-prose-pre-bg': '#1e2637',
              '--tw-prose-th-borders': '#5a6478',
              '--tw-prose-td-borders': '#5a6478',
            },
            {
              color: 'var(--tw-prose-body)',
              fontSize: '1rem',
              lineHeight: 1.6,
              p: {
                marginBottom: '1rem',
                marginTop: '1rem',
              },
              'p > img': {
                borderRadius: theme('borderRadius.xs'),
              },
              a: {
                color: 'var(--tw-prose-links)',
                fontWeight: 400,
              },
              strong: {
                color: 'var(--tw-prose-bold)',
                fontWeight: 600,
              },
              ol: {
                listStyleType: 'var(--list-counter-style)',
                marginBottom: '1rem',
                marginTop: '1rem',
                paddingLeft: '1.25rem',
              },
              ul: {
                listStyleType: 'disc',
                marginBottom: '1rem',
                marginTop: '1rem',
                paddingLeft: '1.5rem',
              },
              'p + ol, p + ul': {
                marginTop: '-0.5rem',
              },
              'ol > li::marker': {
                color: 'var(--tw-prose-counters)',
              },
              'ul > li::marker': {
                color: 'var(--tw-prose-bullets)',
              },
              'ol > li::before, ul > li::before': {
                // Overrides a style that comes from an unclear place (not @tailwindcss/typography defaults):
                content: 'none',
              },
              li: {
                marginBottom: 0,
                marginTop: 0,
              },
              'ol > li': {
                paddingLeft: '0.25rem',
              },
              'ul > li': {
                paddingLeft: 0,
              },
              '> ul > li p': {
                marginBottom: 0,
                marginTop: 0,
              },
              '> ol > li > *:first-child, > ul > li > *:first-child': {
                marginTop: 0,
              },
              '> ol > li > *:last-child, > ul > li > *:last-child': {
                marginBottom: 0,
              },
              'ul ul, ul ol, ol ul, ol ol': {
                marginBottom: 0,
                marginTop: 0,
              },
              hr: {
                borderColor: 'var(--tw-prose-hr)',
                marginBottom: '2rem',
                marginTop: '2rem',
              },
              'h1 + hr, h2 + hr, h3 + hr, h4 + hr, h5 + hr, h6 + hr': {
                marginTop: '-1rem',
              },
              blockquote: {
                color: 'var(--tw-prose-quotes)',
                borderLeftColor: 'var(--tw-prose-quote-borders)',
                borderLeftWidth: '2px',
                fontStyle: 'unset',
                fontWeight: 'unset',
                marginBottom: '1.5rem',
                marginTop: '1.5rem',
                paddingLeft: '1rem',
                quotes: 'none',
              },
              h1: {
                color: 'var(--tw-prose-headings)',
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: '1.3125rem',
                marginBottom: '1rem',
                marginTop: 0,
              },
              'h1 strong': {
                fontWeight: 800,
              },
              h2: {
                color: 'var(--tw-prose-headings)',
                fontSize: '1.125rem',
                fontWeight: 400,
                lineHeight: '1.3125rem',
                marginBottom: '0.9rem',
                marginTop: '1rem',
              },
              'h2 strong': {
                fontWeight: 800,
              },
              h3: {
                color: 'var(--tw-prose-headings)',
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: '1.3125rem',
                marginBottom: '0.8rem',
                marginTop: '1rem',
              },
              'h3 strong': {
                fontWeight: 800,
              },
              'h4, h5, h6': {
                color: 'var(--tw-prose-headings)',
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: '1.3125rem',
                marginBottom: '0.7rem',
                marginTop: '1rem',
              },
              'h1 + *, h2 + *, h3 + *, h4 + *, h5 + *, h6 + *': {
                marginTop: 0,
              },
              'img, video, figure': {
                marginBottom: '1.5rem',
                marginTop: '1.5rem',
              },
              figcaption: {
                color: 'var(--tw-prose-captions)',
              },
              code: {
                backgroundColor: 'var(--tw-prose-pre-bg)',
                borderRadius: theme('borderRadius.xs'),
                color: 'var(--tw-prose-code)',
                fontFamily: theme('fontFamily.mono'),
                fontSize: '87.5%',
                fontWeight: 'unset',
                padding: '0.25rem',
              },
              'code::before': {
                content: 'none',
              },
              'code::after': {
                content: 'none',
              },
              'a code': {
                color: 'var(--tw-prose-links)',
              },
              'h2 code, h3 code, h4 code, h5 code, h6 code': {
                fontSize: '87.5%',
              },
              pre: {
                backgroundColor: 'var(--tw-prose-pre-bg)',
                borderRadius: theme('borderRadius.xs'),
                color: 'var(--tw-prose-pre-code)',
                fontFamily: theme('fontFamily.mono'),
                fontSize: '0.875rem',
                fontWeight: 'unset',
                lineHeight: 2,
                marginBottom: '1.5rem',
                marginTop: '1.5rem',
                paddingBottom: '0.5rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                paddingTop: '0.5rem',
              },
              table: {
                fontSize: 'unset',
                lineHeight: 'unset',
                marginBottom: '1.5rem',
                marginTop: '1.5rem',
              },
              'thead, tbody tr:nth-child(even)': {
                backgroundColor: '#272F40',
              },
              thead: {
                borderBottomColor: 'var(--tw-prose-th-borders)',
                color: 'var(--tw-prose-body)',
              },
              'thead th': {
                borderColor: 'var(--tw-prose-th-borders)',
                borderLeftWidth: '1px',
                borderRightWidth: '1px',
                fontWeight: 'normal',
                paddingBottom: '2px',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
                paddingTop: '2px',
              },
              'thead th:first-child': {
                borderLeftWidth: 0,
                paddingLeft: '0.5rem',
              },
              'thead th:last-child': {
                borderRightWidth: 0,
                paddingRight: '0.5rem',
              },
              'tbody tr': {
                borderBottomColor: 'var(--tw-prose-td-borders)',
              },
              'tbody td': {
                borderColor: 'var(--tw-prose-td-borders)',
                borderLeftWidth: '1px',
                borderRightWidth: '1px',
                paddingBottom: '2px',
                paddingLeft: '0.5rem',
                paddingRight: '0.5rem',
                paddingTop: '2px',
              },
              'tbody td:first-child': {
                borderLeftWidth: 0,
                paddingLeft: '0.5rem',
              },
              'tbody td:last-child': {
                borderRightWidth: 0,
                paddingRight: '0.5rem',
              },
            },
          ],
        },
      }),
    },
  },
  plugins: [
    require('@tailwindcss/aspect-ratio'),
    require('@tailwindcss/forms'),
    require('@tailwindcss/typography'),
    require('./component-lib/plugins/text-styles.plugin'),
  ],
};
